import { ChangeDetectionStrategy, Component, Input, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, interval, Observable, ReplaySubject, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, scan, switchMap, take, takeUntil, throttleTime } from 'rxjs/operators';
import { MessageConversation } from 'common/store/genesys-cloud/conversations/messages/models/message-conversation.model';
import { selectContactById } from 'common/store/genesys-cloud/external-contacts/selectors/external-contact.selectors';
import { ThemeService } from 'common/theme/theme.service';
import { acceptMessageConversation } from 'common/store/genesys-cloud/conversations/messages/actions/message-conversation.actions';
import { openConversationWorkItem } from 'common/store/genesys-cloud/conversations/common/actions/conversation-work-item.actions';
import {
  selectLastTwoMessagesByConversationId,
  selectUnansweredMessageCountByConversationId
} from 'common/store/genesys-cloud/conversations/messages/selectors/message.selectors';
import {
  MessageData,
  MessageDirection
} from 'common/store/genesys-cloud/conversations/messages/models/message-data.model';
import {
  selectMessageConversationIsAssignedByQueue,
  selectMessageConversationStateIsOpen,
  selectMessageConversationTypeById
} from 'common/store/genesys-cloud/conversations/messages/selectors/message-conversation.selectors';
import { BcConfigService } from 'common/config/provider';
import { selectIsRichTextEnabled } from 'common/store/genesys-cloud/feature-toggles/selectors/footer-settings-selector';

interface SelectConversationEvent {
  conversation: MessageConversation;
  focus: boolean;
}

@Component({
  selector: 'app-work-item-genesys-chat-list-item',
  templateUrl: './workitem-genesys-chat-list-item.component.html',
  styleUrls: ['./workitem-genesys-chat-list-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkitemGenesysChatListItemComponent implements OnDestroy {
  static readonly SelectActionThrottleTimeMs = 300;
  private static readonly alertingTimeoutRefreshIntervalInMilliSeconds = 1000;
  private readonly onDestroySubject = new Subject<void>();
  private readonly conversationSubject = new ReplaySubject<MessageConversation>(1);
  private readonly onSelectSubject = new Subject<SelectConversationEvent>();
  private readonly conversationId$ = this.conversationSubject.pipe(
    map((conversation) => conversation.id),
    distinctUntilChanged()
  );
  readonly conversation$ = this.conversationSubject.asObservable();
  readonly avatarThemeId$ = this.conversationId$.pipe(
    map((conversationId) => this.themeService.getAvatarThemeId(conversationId))
  );
  readonly themeCssClassName$ = this.conversationId$.pipe(
    map((conversationId) => this.themeService.getThemeId(conversationId)),
    map((themeId) => this.themeService.getThemeCssClassName(themeId))
  );
  readonly contact$ = this.conversation$.pipe(
    map((conversation) => conversation.customerParticipant.externalContactId),
    distinctUntilChanged(),
    switchMap((contactId) => this.store.select(selectContactById(contactId)))
  );
  readonly lastMessages$ = this.conversationId$.pipe(
    switchMap((conversationId) => this.store.select(selectLastTwoMessagesByConversationId(conversationId)))
  );
  readonly unansweredMessageCount$ = this.conversationId$.pipe(
    switchMap((conversationId) => this.store.select(selectUnansweredMessageCountByConversationId(conversationId)))
  );
  readonly conversationType$ = this.conversationId$.pipe(
    switchMap((conversationId) => this.store.select(selectMessageConversationTypeById(conversationId)))
  );
  readonly isConversationOpen$ = this.conversationId$.pipe(
    switchMap((conversationId) => this.store.select(selectMessageConversationStateIsOpen(conversationId)))
  );
  readonly isConversationAcceptable$ = this.conversation$.pipe(map((item) => item.isAcceptable));

  readonly isConversationAssignedByQueue$ = this.conversationId$.pipe(
    switchMap((conversationId) => this.store.select(selectMessageConversationIsAssignedByQueue(conversationId)))
  );

  readonly isSecondsRemainingVisible$ = combineLatest([
    this.isConversationAcceptable$,
    this.isConversationAssignedByQueue$
  ]).pipe(map(([isAcceptable, isAssignedByQueue]) => isAcceptable && isAssignedByQueue));

  readonly shouldFormatMessage$ = this.conversationId$.pipe(
    switchMap((conversationId) =>
      this.store.select(selectIsRichTextEnabled(conversationId, this.config.current.genesysRichTextEnabledProviders))
    )
  );

  @Input()
  set conversation(newValue: MessageConversation) {
    this.conversationSubject.next(newValue);
  }
  @Input()
  isOpened = false;
  @Input()
  isFocused = false;

  readonly conversationAlertingTimeout$: Observable<number> = this.conversation$.pipe(
    filter((conv) => !!conv),
    distinctUntilChanged((prev, cur) => prev.isAcceptable === cur.isAcceptable),
    switchMap((conv) => this.startRemainingTimer(conv.agentParticipant.alertingTimeoutMs))
  );

  constructor(
    readonly store: Store,
    private readonly themeService: ThemeService,
    private readonly config: BcConfigService
  ) {
    this.onSelectSubject
      .pipe(
        throttleTime(WorkitemGenesysChatListItemComponent.SelectActionThrottleTimeMs),
        takeUntil(this.onDestroySubject)
      )
      .subscribe((onSelectEvent) => this.dispatchConversationAction(onSelectEvent));
  }

  ngOnDestroy(): void {
    this.onDestroySubject.next();
    this.onDestroySubject.complete();
  }

  selectConversation(conversation: MessageConversation, focus: boolean): void {
    this.onSelectSubject.next({ conversation, focus });
  }

  isCustomerMessage(messageData: MessageData): boolean {
    return messageData.direction === MessageDirection.Inbound;
  }

  private dispatchConversationAction({ conversation, focus }: SelectConversationEvent): void {
    const action = conversation.isAcceptable
      ? acceptMessageConversation({ conversationId: conversation.id, focus })
      : openConversationWorkItem({ conversationId: conversation.id, focus });
    this.store.dispatch(action);
  }

  startRemainingTimer(alertingTimeout: number): Observable<number> {
    return interval(WorkitemGenesysChatListItemComponent.alertingTimeoutRefreshIntervalInMilliSeconds).pipe(
      scan(
        (acc) => --acc,
        alertingTimeout / WorkitemGenesysChatListItemComponent.alertingTimeoutRefreshIntervalInMilliSeconds
      ),
      take(alertingTimeout / WorkitemGenesysChatListItemComponent.alertingTimeoutRefreshIntervalInMilliSeconds)
    );
  }
}
